/**
 * @file AdminNotifications.jsx — /cx/notificaciones
 */
import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw } from "lucide-react";
import { adminService } from "../services/admin";
import { ADMIN_STRINGS } from "../constants/strings";
import {
  Button,
  Card,
  Chip,
  Input,
  Select,
  SelectItem,
  Spinner,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  Tooltip,
  toast,
} from "../ui";
import "./AdminGeneric.css";

const STATUS_TONES = { SENT: "ok", FAILED: "err", PENDING: "warn" };
const STATUS_LABELS = {
  SENT: ADMIN_STRINGS.NOTIF_STATUS_SENT,
  FAILED: ADMIN_STRINGS.NOTIF_STATUS_FAILED,
  PENDING: ADMIN_STRINGS.NOTIF_STATUS_PENDING,
};

const TYPE_TONES = { WHATSAPP: "ok", EMAIL: "primary", PUSH: "warn" };

export default function AdminNotifications() {
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {};
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const res = await adminService.getNotifications(params);
      setEntries(res.data || []);
    } catch {
      // ignore
    } finally {
      setIsLoading(false);
    }
  }, [typeFilter, statusFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRetry = async (id) => {
    try {
      await adminService.retryNotification(id);
      toast.success("Notificación marcada para reintento");
      fetchData();
    } catch {
      // ignore
    }
  };

  return (
    <div className="ag-page">
      <header className="ag-head">
        <span className="ag-eyebrow">Admin · Notificaciones</span>
        <h1 className="ag-title">{ADMIN_STRINGS.NOTIF_TITLE}</h1>
      </header>

      <div className="ag-filters">
        <Select
          label={ADMIN_STRINGS.NOTIF_FILTER_TYPE}
          value={typeFilter}
          onValueChange={(v) => setTypeFilter(v === "__all__" ? "" : v)}
          placeholder={ADMIN_STRINGS.NOTIF_ALL_TYPES}
        >
          <SelectItem value="__all__">{ADMIN_STRINGS.NOTIF_ALL_TYPES}</SelectItem>
          <SelectItem value="WHATSAPP">{ADMIN_STRINGS.NOTIF_TYPE_WHATSAPP}</SelectItem>
          <SelectItem value="EMAIL">{ADMIN_STRINGS.NOTIF_TYPE_EMAIL}</SelectItem>
          <SelectItem value="PUSH">{ADMIN_STRINGS.NOTIF_TYPE_PUSH}</SelectItem>
        </Select>
        <Select
          label={ADMIN_STRINGS.NOTIF_FILTER_STATUS}
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v === "__all__" ? "" : v)}
          placeholder={ADMIN_STRINGS.NOTIF_ALL_STATUSES}
        >
          <SelectItem value="__all__">{ADMIN_STRINGS.NOTIF_ALL_STATUSES}</SelectItem>
          <SelectItem value="SENT">{ADMIN_STRINGS.NOTIF_STATUS_SENT}</SelectItem>
          <SelectItem value="FAILED">{ADMIN_STRINGS.NOTIF_STATUS_FAILED}</SelectItem>
          <SelectItem value="PENDING">{ADMIN_STRINGS.NOTIF_STATUS_PENDING}</SelectItem>
        </Select>
        <Input type="date" label="Desde" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        <Input type="date" label="Hasta" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
      </div>

      {isLoading ? (
        <div className="ag-loading">
          <Spinner size={28} />
        </div>
      ) : entries.length === 0 ? (
        <Card className="ag-empty">
          <p>{ADMIN_STRINGS.NOTIF_EMPTY}</p>
        </Card>
      ) : (
        <Card padded={false} className="ag-table-wrap">
          <Table>
            <THead>
              <TR>
                <TH>Fecha</TH>
                <TH>Tipo</TH>
                <TH>Destinatario</TH>
                <TH>Plantilla</TH>
                <TH>Estado</TH>
                <TH>Error</TH>
                <TH align="right">Acciones</TH>
              </TR>
            </THead>
            <TBody>
              {entries.map((e) => (
                <TR key={e.id}>
                  <TD>
                    <span className="ag-date">
                      {new Date(e.createdAt).toLocaleString("es-MX", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </span>
                  </TD>
                  <TD>
                    <Chip tone={TYPE_TONES[e.type] || "neutral"} size="sm">
                      {ADMIN_STRINGS[`NOTIF_TYPE_${e.type}`] || e.type}
                    </Chip>
                  </TD>
                  <TD>{e.recipient}</TD>
                  <TD>{e.messageTemplate || "—"}</TD>
                  <TD>
                    <Chip tone={STATUS_TONES[e.status] || "neutral"} size="sm">
                      {STATUS_LABELS[e.status] || e.status}
                    </Chip>
                  </TD>
                  <TD>
                    {e.errorMessage ? (
                      <Tooltip content={e.errorMessage}>
                        <span className="ag-truncate" style={{ color: "var(--err-text)" }}>
                          {e.errorMessage}
                        </span>
                      </Tooltip>
                    ) : (
                      "—"
                    )}
                  </TD>
                  <TD align="right">
                    {e.status === "FAILED" && (
                      <Button
                        size="sm"
                        variant="outline"
                        leftIcon={<RefreshCw size={12} />}
                        onClick={() => handleRetry(e.id)}
                      >
                        {ADMIN_STRINGS.NOTIF_RETRY}
                      </Button>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
